@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import com.fsryan.foundation.data.FSSyncEvent
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The stored-mix writes, share-link creation and the public shared-mix fetch.
 *
 * One nested sealed triple per operation (sync data source playbook, Step 2). Every event carries
 * the operation's inputs and nothing it produced: outcomes are written to a local data source and
 * observed there. `Failure` adds the error. Constructors are public because the backend variant
 * module constructs these events (controlled-construction playbook: sync-event lifecycle variants).
 */
sealed interface WMMixSyncEvent<out E: WMMixSyncEvent<E>>: FSSyncEvent<E> {

    /** Create a stored mix; the new id becomes the user's selected mix. */
    sealed interface SaveMix<out E: SaveMix<E>>: WMMixSyncEvent<E> {
        val uid: String
        val title: String

        data class Initial(
            override val uid: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SaveMix<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SaveMix<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val title: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SaveMix<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Replace the occupations, filters and title of [mixId]. */
    sealed interface UpdateMix<out E: UpdateMix<E>>: WMMixSyncEvent<E> {
        val uid: String
        val mixId: String
        val title: String

        data class Initial(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UpdateMix<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UpdateMix<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UpdateMix<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Change the title of [mixId]. */
    sealed interface RenameMix<out E: RenameMix<E>>: WMMixSyncEvent<E> {
        val uid: String
        val mixId: String
        val title: String

        data class Initial(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RenameMix<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RenameMix<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RenameMix<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Copy [mixId] under [title]; the copy becomes the selected mix. */
    sealed interface DuplicateMix<out E: DuplicateMix<E>>: WMMixSyncEvent<E> {
        val uid: String
        val mixId: String
        val title: String

        data class Initial(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DuplicateMix<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DuplicateMix<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val mixId: String,
            override val title: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DuplicateMix<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Soft-delete [mixId]. */
    sealed interface DeleteMix<out E: DeleteMix<E>>: WMMixSyncEvent<E> {
        val uid: String
        val mixId: String

        data class Initial(
            override val uid: String,
            override val mixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DeleteMix<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val mixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DeleteMix<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val mixId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): DeleteMix<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Create a share link for [mixId]; the link lands in the user's local context under [mixId]. */
    sealed interface CreateShare<out E: CreateShare<E>>: WMMixSyncEvent<E> {
        val uid: String
        val mixId: String

        data class Initial(
            override val uid: String,
            override val mixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): CreateShare<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val mixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): CreateShare<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val mixId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): CreateShare<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /**
     * Ask the API to render [sharedMixId]'s preview image again. Success means the render started; the image, or its
     * failure, arrives through the mirror of the user's shares.
     */
    sealed interface RegenerateSharePreview<out E: RegenerateSharePreview<E>>: WMMixSyncEvent<E> {
        val uid: String
        val sharedMixId: String

        data class Initial(
            override val uid: String,
            override val sharedMixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RegenerateSharePreview<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val sharedMixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RegenerateSharePreview<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val sharedMixId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): RegenerateSharePreview<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Turn the user's share link [sharedMixId] on or off; the mirrored owner record shows the result. */
    sealed interface SetShareActive<out E: SetShareActive<E>>: WMMixSyncEvent<E> {
        val uid: String
        val sharedMixId: String
        val active: Boolean

        data class Initial(
            override val uid: String,
            override val sharedMixId: String,
            override val active: Boolean,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SetShareActive<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val sharedMixId: String,
            override val active: Boolean,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SetShareActive<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val sharedMixId: String,
            override val active: Boolean,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SetShareActive<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Mirror the public document for [sharedMixId] into the global local context until `stopObservingSharedMix`; Success is the first snapshot. */
    sealed interface ObserveSharedMix<out E: ObserveSharedMix<E>>: WMMixSyncEvent<E> {
        val sharedMixId: String

        data class Initial(
            override val sharedMixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ObserveSharedMix<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val sharedMixId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ObserveSharedMix<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val sharedMixId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ObserveSharedMix<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }
}
